import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Moon, Sun, User, LogOut, LayoutDashboard, Menu, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { Button } from "./ui/button";

const navItems = [
  { to: "/", label: "Accueil" },
  { to: "/reserver", label: "Réserver" },
  { to: "/tarifs", label: "Tarifs" },
  { to: "/faq", label: "FAQ" },
  { to: "/a-propos", label: "À propos" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  return (
    <header className="sticky top-0 z-40 glass-nav" data-testid="navbar">
      <div className="container-x flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <img
            src="https://customer-assets-gfyr7b9c.emergentagent.net/job_horizon-book-1/artifacts/ws7lgoor_SYNWOR.webp"
            alt="Synergie Workspace"
            className="h-11 w-auto mix-blend-multiply dark:mix-blend-screen dark:invert"
          />
          <span className="font-heading font-semibold tracking-tight text-lg">Synergie Workspace</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${isActive ? "text-[#2B8B8E]" : "text-foreground/80 hover:text-[#2B8B8E]"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <button
            onClick={toggle}
            data-testid="theme-toggle"
            className="p-2 rounded-full border border-border hover:bg-muted transition"
            aria-label="Changer le thème"
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          {user ? (
            <>
              {user.role === "admin" && (
                <Button asChild variant="outline" size="sm" data-testid="nav-admin-btn">
                  <Link to="/admin"><LayoutDashboard size={14} className="mr-1" /> Admin</Link>
                </Button>
              )}
              <Button asChild variant="outline" size="sm" data-testid="nav-account-btn">
                <Link to="/mon-compte"><User size={14} className="mr-1" /> Mon compte</Link>
              </Button>
              <Button
                variant="ghost" size="sm"
                data-testid="nav-logout-btn"
                onClick={async () => { await logout(); navigate("/"); }}
              >
                <LogOut size={14} />
              </Button>
            </>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm" data-testid="nav-login-btn">
                <Link to="/connexion">Connexion</Link>
              </Button>
              <Button asChild size="sm" className="bg-[#2B8B8E] hover:bg-[#1F6F73] text-white" data-testid="nav-register-btn">
                <Link to="/inscription">Inscription</Link>
              </Button>
            </>
          )}
        </div>

        <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-border bg-background" data-testid="nav-mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-4">
            {navItems.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} className="text-sm font-medium">{n.label}</NavLink>
            ))}
            <div className="flex gap-2 pt-2 border-t border-border">
              <button onClick={toggle} className="p-2 rounded border border-border">
                {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
              </button>
              {user ? (
                <>
                  <Link to="/mon-compte" className="text-sm">Mon compte</Link>
                  {user.role === "admin" && <Link to="/admin" className="text-sm">Admin</Link>}
                  <button onClick={async () => { await logout(); navigate("/"); }} className="text-sm">Déconnexion</button>
                </>
              ) : (
                <>
                  <Link to="/connexion" className="text-sm">Connexion</Link>
                  <Link to="/inscription" className="text-sm font-semibold text-[#2B8B8E]">Inscription</Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
